import {
  ActionConfirmationModal,
  Button,
  Form,
  FormSubmitButton,
  SelectFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterIntakeTemplateQuestion,
  useDeleteMatterIntakeTemplateQuestion,
  useUpdateMatterIntakeTemplateQuestion,
} from "@/lib/data/react-query/matters/matter-intake-template-question";
import { MatterIntakeTemplateQuestionSchema } from "@/lib/data/schemas";
import { MATTER_INTAKE_TEMPLATE_QUESTION_TYPE_OPTIONS } from "@/lib/data/static/matters/intakeQuestionStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type MatterIntakeTemplate,
  type MatterIntakeTemplateQuestion,
} from "@/lib/types";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface AddOrEditMatterIntakeTemplateQuestionModalProps {
  editing: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  template: MatterIntakeTemplate;
  question?: MatterIntakeTemplateQuestion;
  isReadOnly?: boolean;
}

const formSchema = MatterIntakeTemplateQuestionSchema.pick({
  label: true,
  type: true,
  order: true,
}).extend({
  options: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const defaultFormValues = {
  label: "",
  type: "Input" as const,
  options: "",
  order: null,
};

const QUESTION_TYPE_OPTIONS = MATTER_INTAKE_TEMPLATE_QUESTION_TYPE_OPTIONS.map(
  (type) => ({
    value: type,
    label: type,
  }),
);

const FORM_ID = "matter-intake-template-question-form";

export const AddOrEditMatterIntakeTemplateQuestionModal = ({
  editing,
  open,
  setOpen,
  template,
  question,
  isReadOnly,
}: AddOrEditMatterIntakeTemplateQuestionModalProps) => {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: editing
      ? {
          ...defaultFormValues,
          ...question,
          options: Array.isArray(question?.options)
            ? question.options
                .filter(
                  (option): option is string => typeof option === "string",
                )
                .join(",")
            : "",
        }
      : defaultFormValues,
    mode: "onSubmit",
  });

  const createQuestion = useCreateMatterIntakeTemplateQuestion();
  const updateQuestion = useUpdateMatterIntakeTemplateQuestion();
  const deleteQuestion = useDeleteMatterIntakeTemplateQuestion();

  const isLoading =
    createQuestion.isPending ||
    updateQuestion.isPending ||
    deleteQuestion.isPending;

  const questionType = form.watch("type");

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (editing) {
      if (!question) {
        console.error("Active question is undefined");
        return;
      }
      await updateQuestion.mutateAsync({
        ...data,
        id: question.id,
        templateId: template.id,
        options: data.options?.split(",").map((option) => option.trim()),
      });
    } else {
      await createQuestion.mutateAsync({
        ...data,
        templateId: template.id,
        options: data.options?.split(",").map((option) => option.trim()),
      });
    }

    if (closeAfterSave) {
      setOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!question) {
      console.error("Active question is undefined");
      return;
    }
    await deleteQuestion.mutateAsync({
      id: question.id,
      templateId: template.id,
    });
    setIsDeleting(false);
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      form.reset(defaultFormValues);
      setCloseAfterSave(false);
    }
  }, [open, form]);

  const canDelete =
    !isReadOnly &&
    editing &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterIntakeTemplateQuestion,
      ACTION_PERMISSION_VALUES.delete,
    );

  const deleteButton = canDelete ? (
    <div>
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    </div>
  ) : null;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={editing ? "Edit question" : "Add question"}
          isDirty={form.formState.isDirty}
          loading={isLoading}
          size="xl"
          overlayClassName="z-[108]"
          className="z-[109]"
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {deleteButton}
              <div className="flex grow justify-end space-x-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                {!isReadOnly && (
                  <>
                    <FormSubmitButton
                      submitFormId={FORM_ID}
                      variant="light-primary"
                      loading={isLoading}
                      submitFn={() => {
                        setCloseAfterSave(false);
                      }}
                    />
                    <FormSubmitButton
                      submitFormId={FORM_ID}
                      variant="default"
                      loading={isLoading}
                      submitFn={() => {
                        setCloseAfterSave(true);
                      }}
                      title="Save and close"
                    />
                  </>
                )}
              </div>
            </div>
          }
        >
          <form
            id={FORM_ID}
            className="flex min-h-0 grow flex-col overflow-hidden"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await form.handleSubmit(onSubmit)();
            }}
          >
            <div className="overflow-y-auto">
              <fieldset
                disabled={isReadOnly}
                className={slideoverFieldsetClassName}
              >
                <TextFormField
                  label="Question label*"
                  placeholder="Enter question label"
                  register={form.register("label")}
                />

                <div className="col-span-2">
                  <SelectFormField
                    label="Question type*"
                    register={form.register("type")}
                    items={QUESTION_TYPE_OPTIONS}
                    itemsKey="value"
                    formatItemLabel={(item) => item?.label}
                    placeholder="Select question type"
                  />
                </div>

                {questionType === "Multiple Choice" && (
                  <div className="col-span-2">
                    <label className="text-sm font-medium">Options</label>
                    <div className="mt-2 space-y-2">
                      <Typography
                        variant="subHeading"
                        size="sm"
                        className="text-muted-foreground"
                      >
                        Enter options separated by commas
                      </Typography>
                      <textarea
                        {...form.register("options")}
                        placeholder="Option 1, Option 2, Option 3"
                        className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring min-h-[80px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                  </div>
                )}

                <div className="col-span-2">
                  <TextFormField
                    label="Order"
                    type="number"
                    placeholder="0"
                    register={form.register("order", {
                      valueAsNumber: true,
                    })}
                  />
                </div>
              </fieldset>
            </div>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title="Are you sure you want to delete this question?"
        open={isDeleting}
        setOpen={setIsDeleting}
        afterActionFn={handleDelete}
      />
    </>
  );
};
